import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Building2, ArrowUpCircle, ArrowDownCircle, Wrench, FileSignature, FileX,
  BellRing, FileText,
} from "lucide-react";
import { formatMoney, formatUSD } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { TIMELINE } from "@/constants/testIds";

const ICON_MAP = {
  "property_created": Building2,
  "income": ArrowUpCircle,
  "expense": ArrowDownCircle,
  "maintenance": Wrench,
  "contract_start": FileSignature,
  "contract_end": FileX,
  "alert": BellRing,
  "document": FileText,
};

const ICON_TONE = {
  "property_created": "text-sky-500 bg-sky-500/10",
  "income": "text-emerald-500 bg-emerald-500/10",
  "expense": "text-rose-500 bg-rose-500/10",
  "maintenance": "text-amber-500 bg-amber-500/10",
  "contract_start": "text-violet-500 bg-violet-500/10",
  "contract_end": "text-muted-foreground bg-secondary",
  "alert": "text-amber-500 bg-amber-500/10",
  "document": "text-muted-foreground bg-secondary",
};

export default function PropertyTimeline({ propertyId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/timeline/${propertyId}`).then((r) => {
      setEvents(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [propertyId]);

  if (loading) return <div className="text-muted-foreground text-sm">Cargando timeline…</div>;
  if (!events.length) return <div className="text-muted-foreground text-sm">Sin eventos.</div>;

  return (
    <div data-testid={TIMELINE.list} className="relative">
      <div className="absolute left-4 top-2 bottom-2 w-px bg-border/60" aria-hidden="true" />
      <ul className="space-y-4">
        {events.map((e, i) => {
          const Icon = ICON_MAP[e.type] || FileText;
          const tone = ICON_TONE[e.type] || "text-muted-foreground bg-secondary";
          return (
            <li key={i} className="relative pl-11 animate-fade-in-up" style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}>
              <div className={`absolute left-0 top-1 w-8 h-8 rounded-full grid place-items-center ${tone}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="rounded-lg border border-border/60 bg-card p-3">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="text-sm font-medium">{e.title}</div>
                  <div className="text-[11px] text-muted-foreground num shrink-0">{e.date ? formatDMY(e.date) : "—"}</div>
                </div>
                {e.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.description}</div>}
                {e.amount != null && (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="num">{formatMoney(e.amount, e.currency)}</span>
                    <span className="text-muted-foreground num">≈ {formatUSD(e.amount_usd)}</span>
                  </div>
                )}
                {e.cost_usd != null && e.type === "maintenance" && (
                  <div className="mt-2 text-xs num">Costo: {formatUSD(e.cost_usd)}</div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
